<template>
    <back-to-home />
    <page-title :title="t('profile.profilePageTitle')" />
    <card-skeleton>
        <card-title :text="t('profile.profileTitle')">
            <card-button data-id="profileEditCancelButton" class="pa-2 mr-2" size="large" @click="goBack">
                {{ t('profile.cancel') }}
            </card-button>
            <card-button
                data-id="profileEditSaveButton"
                class="pa-2"
                size="large"
                variant="flat"
                color="primary"
                :loading="isSubmitting"
                @click="handleSubmit"
            >
                {{ t('profile.profileSave') }}
            </card-button>
        </card-title>
        <v-sheet class="mt-4">
            <v-form ref="form" @submit.prevent="handleSubmit">
                <form-section :is-loading="isLoading" class-name="mb-0">
                    <field-group>
                        <input-field
                            id="firstName"
                            v-model="formData.firstName"
                            data-id="profileEditFirstNameField"
                            :label="t('register.firstName')"
                            :required="true"
                            :disabled="isSubmitting"
                        />
                        <input-field
                            id="lastName"
                            v-model="formData.lastName"
                            data-id="profileEditLastNameField"
                            :label="t('register.lastName')"
                            :required="true"
                            :disabled="isSubmitting"
                        />
                    </field-group>
                    <input-field
                        id="phone"
                        v-model="formData.phone"
                        data-id="profileEditPhoneField"
                        :label="t('register.phoneNumber')"
                        :readonly="true"
                        :disabled="isSubmitting"
                        icon="mdi-pencil"
                        append-inner-icon="mdi-pencil"
                        @click="openDialog(PROFILE_FORM_TYPES.EDIT_PHONE)"
                    />
                    <input-field
                        id="email"
                        v-model="formData.email"
                        data-id="profileEditEmailField"
                        :pattern="FIELD_PATTERNS.EMAIL"
                        :pattern-error="t('validation.emailRequired')"
                        :readonly="true"
                        :disabled="isSubmitting"
                        :label="t('register.email')"
                        icon="mdi-pencil"
                        @click="openDialog(PROFILE_FORM_TYPES.EDIT_EMAIL)"
                    />
                    <input-field
                        v-if="userStore.profile.workEmail"
                        id="workEmail"
                        v-model="userStore.profile.workEmail"
                        :pattern="FIELD_PATTERNS.EMAIL"
                        :pattern-error="t('validation.emailRequired')"
                        :readonly="true"
                        :disabled="true"
                        :label="t('profile.workEmail')"
                    />
                    <input-field
                        id="password"
                        placeholder="*********"
                        data-id="profileEditPasswordField"
                        :type="FIELD_TYPES.PASSWORD"
                        :disabled="isSubmitting"
                        :readonly="true"
                        icon="mdi-pencil"
                        :label="t('register.password')"
                        @click="openDialog(PROFILE_FORM_TYPES.EDIT_PASSWORD)"
                    />
                </form-section>
                <form-section :is-loading="isLoading || isDeleting" class-name="text-center mb-4">
                    <card-button
                        data-id="profileEditDeleteProfileButton"
                        variant="plain"
                        border="none"
                        color="error"
                        prepend-icon="mdi-delete"
                        size="large"
                        @click="openDialog(PROFILE_FORM_TYPES.DELETE_PROFILE)"
                    >
                        {{ t('profile.deleteProfile') }}
                    </card-button>
                </form-section>
            </v-form>
        </v-sheet>
        <edit-email-dialog :title="t('profile.editEmail')" :type="PROFILE_FORM_TYPES.EDIT_EMAIL" />
        <edit-phone-dialog :title="t('profile.editPhone')" :type="PROFILE_FORM_TYPES.EDIT_PHONE" />
        <edit-password-dialog :title="t('profile.changePassword')" :type="PROFILE_FORM_TYPES.EDIT_PASSWORD" />
        <delete-profile-dialog :title="t('profile.deleteProfile')" :type="PROFILE_FORM_TYPES.DELETE_PROFILE" />
    </card-skeleton>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
    />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@geta/kolumbus-frontend/features/authentication';
import { PhoneDto, UpdateProfileDto } from '@/types/webapi';
import { useContainer } from '@/plugins/inversify';
import { IProfileApi, IProfileApiId } from '@/features/Profile/services/Interface/IProfileApi';
import { routeNames } from '@/router';
import { navigateBack } from '../helpers';
import { validateStringValue } from '@/helpers';
import { DEFAULT_COUNTRY, FIELD_PATTERNS, FIELD_TYPES } from '@/features/common/components/form/constants';
import { BackToHome, PageTitle } from '@/features/common/components/page';
import { FieldGroup, InputField, NotificationBar, FormSection } from '@/features/common/components/form';
import { useFormData, useFormHandling, useNotification } from '@/features/common/components/form/composables';
import { PROFILE_FORM_TYPES } from '../constants';
import CardButton from '@/features/common/components/CardButton.vue';
import CardTitle from '@/features/common/components/CardTitle.vue';
import CardSkeleton from '@/features/common/components/CardSkeleton.vue';
import EditEmailDialog from '@/features/Profile/components/EditEmailDialog.vue';
import { useGlobalDialogStore } from '@/store/dialogStore';
import EditPasswordDialog from '@/features/Profile/components/EditPasswordDialog.vue';
import EditPhoneDialog from '@/features/Profile/components/EditPhoneDialog.vue';
import DeleteProfileDialog from '@/features/Profile/components/DeleteProfileDialog.vue';
import { storeToRefs } from 'pinia';

const { t } = useI18n();
const userStore = useUserStore();
const { profile, user } = storeToRefs(userStore);
const { openDialog } = useGlobalDialogStore();

const container = useContainer();
const profileApi = container.get<IProfileApi>(IProfileApiId);

const isLoading = ref(false);
const isDeleting = ref(false);

interface ProfileFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

const defaultPhone: PhoneDto = { countryCode: DEFAULT_COUNTRY.PHONE_CODE, number: '' };
const userPhone = computed(() => user.value.phone || defaultPhone);

const profileData = {
    firstName: validateStringValue(profile.value.firstName),
    lastName: validateStringValue(profile.value.lastName),
    email: validateStringValue(profile.value.email),
    phone: validateStringValue(userPhone.value.countryCode) + ' ' + validateStringValue(userPhone.value.number)
} as ProfileFormData;

const { notificationBarState, notificationError, notificationSuccess } = useNotification();
const { form, isSubmitting, toggleIsSubmitting, validateForm, isFormEmpty } = useFormHandling();
const { formData } = useFormData(profileData);

const saveData = async (data: ProfileFormData) => {
    try {
        const updateProfileDto = {
            firstName: data.firstName,
            lastName: data.lastName,
            phone: userPhone.value
        } as UpdateProfileDto;

        const updatedProfile = await profileApi.updateProfile(updateProfileDto);

        if (updatedProfile) {
            notificationBarState.value = notificationSuccess(t('profile.profileSaved'));
            await userStore.loadProfile();
            return true;
        } else {
            notificationBarState.value = notificationError(t('profile.updateError'));
            return false;
        }
    } catch {
        notificationBarState.value = notificationError(t('profile.genericError'));
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleIsSubmitting(true);
        await saveData(formData.value);
    } finally {
        toggleIsSubmitting(false);
    }
};

const handleSubmit = async () => {
    if (isFormEmpty()) return;
    const { valid } = await validateForm();
    if (!valid) return;
    await submitForm();
};

const goBack = () => navigateBack(2, -1, routeNames.profile);

// Watch for changes in profile or user and update formData accordingly
watch(
    [profile, user],
    () => {
        formData.value = {
            firstName: validateStringValue(profile.value.firstName),
            lastName: validateStringValue(profile.value.lastName),
            email: validateStringValue(profile.value.email),
            phone: validateStringValue(userPhone.value.countryCode) + ' ' + validateStringValue(userPhone.value.number)
        };
    },
    { deep: true }
);
</script>
